/**
 * useNavigationBlocker Hook
 * Custom hook to block navigation when there are unsaved changes
 * Works with BrowserRouter (not just data routers)
 */

import { useEffect, useContext, useRef, useState } from 'react';
import { UNSAFE_NavigationContext as NavigationContext } from 'react-router-dom';

export const useNavigationBlocker = (shouldBlock, onNavigationAttempt) => {
  const navigator = useContext(NavigationContext).navigator;
  const pendingNavigation = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (!shouldBlock) return;

    const push = navigator.push;
    const replace = navigator.replace;

    // Override push method
    navigator.push = (...args) => {
      pendingNavigation.current = { method: 'push', args };
      setIsModalOpen(true);
      if (onNavigationAttempt) {
        onNavigationAttempt(() => {
          push.apply(navigator, args);
          setIsModalOpen(false);
          pendingNavigation.current = null;
        });
      }
    };

    // Override replace method
    navigator.replace = (...args) => {
      pendingNavigation.current = { method: 'replace', args };
      setIsModalOpen(true);
      if (onNavigationAttempt) {
        onNavigationAttempt(() => {
          replace.apply(navigator, args);
          setIsModalOpen(false);
          pendingNavigation.current = null;
        });
      }
    };

    return () => {
      navigator.push = push;
      navigator.replace = replace;
    };
  }, [navigator, shouldBlock, onNavigationAttempt]);

  const cancelNavigation = () => {
    setIsModalOpen(false);
    pendingNavigation.current = null;
  };

  return { isModalOpen, cancelNavigation };
};

